'use client';

import * as React from 'react';
import { API } from '@pilot/api';
import {
  Room,
  RoomEvent,
  ConnectionState,
  Track,
  Participant,
  LocalParticipant,
  RemoteParticipant,
} from 'livekit-client';

interface MeetingParticipant {
  identity: string;
  name: string;
  isSpeaking: boolean;
  isMuted: boolean;
  isVideoEnabled: boolean;
  isLocal: boolean;
}

interface ActiveMeeting {
  roomId: string;
  roomName: string;
  spaceSlug: string;
  type: 'voice' | 'video';
  serverUrl: string;
  token: string;
}

interface MediaDeviceInfo {
  deviceId: string;
  label: string;
  kind: 'audioinput' | 'videoinput' | 'audiooutput';
}

interface MeetingContextValue {
  activeMeeting: ActiveMeeting | null;
  room: Room | null;
  connectionState: ConnectionState;
  participants: MeetingParticipant[];
  isMuted: boolean;
  isVideoEnabled: boolean;
  audioDevices: MediaDeviceInfo[];
  videoDevices: MediaDeviceInfo[];
  selectedAudioDevice: string | null;
  selectedVideoDevice: string | null;
  joinMeeting: (meeting: ActiveMeeting) => Promise<void>;
  leaveMeeting: () => Promise<void>;
  toggleMute: () => void;
  toggleVideo: () => void;
  switchAudioDevice: (deviceId: string) => Promise<void>;
  switchVideoDevice: (deviceId: string) => Promise<void>;
  refreshDevices: () => Promise<void>;
}

const MeetingContext = React.createContext<MeetingContextValue | null>(null);

export function useMeeting() {
  const context = React.useContext(MeetingContext);
  if (!context) {
    throw new Error('useMeeting must be used within MeetingProvider');
  }
  return context;
}

export function useMeetingOptional() {
  return React.useContext(MeetingContext);
}

interface MeetingProviderProps {
  children: React.ReactNode;
}

export function MeetingProvider({ children }: MeetingProviderProps) {
  const [activeMeeting, setActiveMeeting] = React.useState<ActiveMeeting | null>(null);
  const [room, setRoom] = React.useState<Room | null>(null);
  const [connectionState, setConnectionState] = React.useState<ConnectionState>(ConnectionState.Disconnected);
  const [participants, setParticipants] = React.useState<MeetingParticipant[]>([]);
  const [isMuted, setIsMuted] = React.useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = React.useState(false);
  const [audioDevices, setAudioDevices] = React.useState<MediaDeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = React.useState<MediaDeviceInfo[]>([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = React.useState<string | null>(null);
  const [selectedVideoDevice, setSelectedVideoDevice] = React.useState<string | null>(null);

  const refreshDevices = React.useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audio = devices
        .filter(d => d.kind === 'audioinput')
        .map(d => ({ deviceId: d.deviceId, label: d.label || `Microphone ${d.deviceId.slice(0, 4)}`, kind: 'audioinput' as const }));
      const video = devices
        .filter(d => d.kind === 'videoinput')
        .map(d => ({ deviceId: d.deviceId, label: d.label || `Camera ${d.deviceId.slice(0, 4)}`, kind: 'videoinput' as const }));
      setAudioDevices(audio);
      setVideoDevices(video);
    } catch (error) {
      console.error('Failed to enumerate devices:', error);
    }
  }, []);

  const updateParticipants = React.useCallback((room: Room) => {
    const allParticipants: MeetingParticipant[] = [];

    const addParticipant = (p: Participant, isLocal: boolean) => {
      const audioTrack = p.getTrackPublications().find(t => t.kind === Track.Kind.Audio);
      const videoTrack = p.getTrackPublications().find(t => t.kind === Track.Kind.Video);

      allParticipants.push({
        identity: p.identity,
        name: p.name || p.identity,
        isSpeaking: p.isSpeaking,
        isMuted: !audioTrack || audioTrack.isMuted,
        isVideoEnabled: !!videoTrack && !videoTrack.isMuted,
        isLocal,
      });
    };

    addParticipant(room.localParticipant, true);
    room.remoteParticipants.forEach(p => addParticipant(p, false));

    setParticipants(allParticipants);
  }, []);

  const joinMeeting = React.useCallback(async (meeting: ActiveMeeting) => {
    if (room) {
      await room.disconnect();
    }

    const newRoom = new Room({
      adaptiveStream: true,
      dynacast: true,
    });

    newRoom.on(RoomEvent.ConnectionStateChanged, (state) => {
      setConnectionState(state);
    });

    newRoom.on(RoomEvent.ParticipantConnected, () => updateParticipants(newRoom));
    newRoom.on(RoomEvent.ParticipantDisconnected, () => updateParticipants(newRoom));
    newRoom.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      if (track.kind === Track.Kind.Audio) {
        track.attach();
      }
      updateParticipants(newRoom);
    });
    newRoom.on(RoomEvent.TrackUnsubscribed, (track) => {
      track.detach();
      updateParticipants(newRoom);
    });
    newRoom.on(RoomEvent.TrackMuted, () => updateParticipants(newRoom));
    newRoom.on(RoomEvent.TrackUnmuted, () => updateParticipants(newRoom));
    newRoom.on(RoomEvent.ActiveSpeakersChanged, () => updateParticipants(newRoom));
    newRoom.on(RoomEvent.LocalTrackPublished, () => updateParticipants(newRoom));
    newRoom.on(RoomEvent.LocalTrackUnpublished, () => updateParticipants(newRoom));

    try {
      await newRoom.connect(meeting.serverUrl, meeting.token);

      await newRoom.localParticipant.setMicrophoneEnabled(true);
      if (meeting.type === 'video') {
        await newRoom.localParticipant.setCameraEnabled(true);
        setIsVideoEnabled(true);
      }

      setRoom(newRoom);
      setActiveMeeting(meeting);
      setIsMuted(false);
      updateParticipants(newRoom);
    } catch (error) {
      console.error('Failed to connect to room:', error);
      throw error;
    }
  }, [room, updateParticipants]);

  const leaveMeeting = React.useCallback(async () => {
    if (room) {
      await room.disconnect();
    }

    if (activeMeeting) {
      try {
        await API.meetings.deleteMeeting(activeMeeting.spaceSlug, activeMeeting.roomId);
      } catch (error) {
        console.error('Failed to notify server of leave:', error);
      }
    }

    setRoom(null);
    setActiveMeeting(null);
    setParticipants([]);
    setConnectionState(ConnectionState.Disconnected);
    setIsMuted(false);
    setIsVideoEnabled(false);
  }, [room, activeMeeting]);

  const toggleMute = React.useCallback(() => {
    if (room) {
      const newMuted = !isMuted;
      room.localParticipant.setMicrophoneEnabled(!newMuted);
      setIsMuted(newMuted);
    }
  }, [room, isMuted]);

  const toggleVideo = React.useCallback(() => {
    if (room) {
      const newEnabled = !isVideoEnabled;
      room.localParticipant.setCameraEnabled(newEnabled);
      setIsVideoEnabled(newEnabled);
    }
  }, [room, isVideoEnabled]);

  const switchAudioDevice = React.useCallback(async (deviceId: string) => {
    if (room) {
      try {
        await room.switchActiveDevice('audioinput', deviceId);
        setSelectedAudioDevice(deviceId);
      } catch (error) {
        console.error('Failed to switch audio device:', error);
      }
    }
  }, [room]);

  const switchVideoDevice = React.useCallback(async (deviceId: string) => {
    if (room) {
      try {
        await room.switchActiveDevice('videoinput', deviceId);
        setSelectedVideoDevice(deviceId);
      } catch (error) {
        console.error('Failed to switch video device:', error);
      }
    }
  }, [room]);

  React.useEffect(() => {
    refreshDevices();
  }, [refreshDevices]);

  React.useEffect(() => {
    return () => {
      if (room) {
        room.disconnect();
      }
    };
  }, []);

  return (
    <MeetingContext.Provider
      value={{
        activeMeeting,
        room,
        connectionState,
        participants,
        isMuted,
        isVideoEnabled,
        audioDevices,
        videoDevices,
        selectedAudioDevice,
        selectedVideoDevice,
        joinMeeting,
        leaveMeeting,
        toggleMute,
        toggleVideo,
        switchAudioDevice,
        switchVideoDevice,
        refreshDevices,
      }}
    >
      {children}
    </MeetingContext.Provider>
  );
}

